import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Car, MapPin, Clock, Star } from 'lucide-react';

interface RideDriverTripsProps {
  onBack: () => void;
}

const RideDriverTrips = ({ onBack }: RideDriverTripsProps) => {
  const { user } = useAuth();
  const [rides, setRides] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'week' | 'month'>('all');

  useEffect(() => {
    if (!user) return;
    const fetchRides = async () => {
      setLoading(true);
      let query = supabase
        .from('ride_requests')
        .select('*')
        .eq('driver_id', user.id)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(100);

      if (filter === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        query = query.gte('completed_at', weekAgo.toISOString());
      } else if (filter === 'month') {
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        query = query.gte('completed_at', monthAgo.toISOString());
      }

      const { data } = await query;
      setRides(data || []);
      setLoading(false);
    };
    fetchRides();
  }, [user, filter]);

  const totalEarnings = rides.reduce((sum, r) => sum + (r.final_fare || r.estimated_fare || 0), 0);

  return (
    <div className="min-h-screen bg-white">
      <div className="px-5 pt-6 pb-4">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
          <h2 className="text-2xl font-bold">Trips</h2>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4">
          {(['all', 'week', 'month'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === f ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'
              }`}
            >
              {f === 'all' ? 'All Time' : f === 'week' ? 'This Week' : 'This Month'}
            </button>
          ))}
        </div>

        {/* Summary */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 bg-muted/50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{rides.length}</p>
            <p className="text-xs text-muted-foreground">Trips</p>
          </div>
          <div className="flex-1 bg-muted/50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">D{totalEarnings.toFixed(0)}</p>
            <p className="text-xs text-muted-foreground">Earned</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
          </div>
        ) : rides.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Car className="w-16 h-16 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No trips yet</h3>
            <p className="text-muted-foreground text-sm">Your completed rides will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rides.map(ride => (
              <div key={ride.id} className="border border-border rounded-xl p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">
                    {new Date(ride.completed_at || ride.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="font-bold text-lg">D{(ride.final_fare || ride.estimated_fare)?.toFixed(0)}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 shrink-0" />
                    <p className="text-sm truncate">{ride.pickup_address}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />
                    <p className="text-sm truncate">{ride.dropoff_address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ride.distance_km?.toFixed(1)} km</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ride.ride_type?.replace('_', ' ')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RideDriverTrips;
